import { useState } from "react";
import { toast } from "sonner";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg, fmtDate, money } from "@/lib/api";
import { PageTitle } from "@/components/layout/DashShell";
import { Loader } from "@/components/Blocks";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const run = async (fn, ok, reload) => {
  try { const r = await fn(); toast.success(typeof ok === "function" ? ok(r.data) : ok); reload(); } catch (e) { toast.error(errMsg(e)); }
};
const PRICE_KEYS = [["early", "Early-bird"], ["regular", "Regular"], ["door", "At the door"], ["vip", "VIP"]];

const toLocal = (iso) => { if (!iso) return ""; const d = new Date(iso); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };

const SeasonCard = ({ s, reload }) => {
  const [f, setF] = useState({ name: s.name, opens: toLocal(s.registration_opens_at), prices: { ...s.prices } });
  const save = () => run(() => api.put(`/admin/seasons/${s.slug}`, { name: f.name, registration_opens_at: f.opens ? new Date(f.opens).toISOString() : "",
    prices: Object.fromEntries(Object.entries(f.prices).map(([k, v]) => [k, Number(v)])) }), "Season saved", reload);
  return (
    <div className="panel space-y-4 p-5" data-testid={`season-${s.slug}`}>
      <div className="grid gap-3 md:grid-cols-2">
        <label className="text-sm">Season name<Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid={`season-name-${s.slug}`} /></label>
        <label className="text-sm">Registration opens on (your time)<Input type="datetime-local" value={f.opens} onChange={(e) => setF({ ...f, opens: e.target.value })} data-testid={`season-opens-${s.slug}`} /></label>
      </div>
      <div className="grid gap-3 sm:grid-cols-5">
        {PRICE_KEYS.map(([k, l]) => <label key={k} className="text-sm">{l} (US cents)<Input type="number" value={f.prices[k]} onChange={(e) => setF({ ...f, prices: { ...f.prices, [k]: e.target.value } })} data-testid={`season-price-${s.slug}-${k}`} /><span className="text-xs text-[#475569]">{money(Number(f.prices[k]))}</span></label>)}
        <label className="text-sm">VIP seat limit<Input type="number" value={f.prices.vip_capacity} onChange={(e) => setF({ ...f, prices: { ...f.prices, vip_capacity: e.target.value } })} data-testid={`season-vipcap-${s.slug}`} /></label>
      </div>
      <div className="flex flex-wrap gap-2">
        <button className="btn-navy" onClick={save} data-testid={`season-save-${s.slug}`}>Save season</button>
        <button className="btn-outline" onClick={() => window.confirm("Replace the ticket prices on every class in this season with these prices?") && run(() => api.post(`/admin/seasons/${s.slug}/apply-prices`), (d) => `${d.updated} classes updated`, reload)} data-testid={`season-apply-${s.slug}`}>Apply prices to all classes</button>
        <button className="btn-outline" onClick={() => window.confirm("Open registration now for every published class in this season?") && run(() => api.post(`/admin/seasons/${s.slug}/open-now`, {}), (d) => `${d.opened} classes opened`, reload)} data-testid={`season-open-${s.slug}`}>Open registration now (all)</button>
      </div>
      <table className="w-full text-sm"><tbody>
        {s.events.map((e) => (
          <tr key={e.id} className="border-t" data-testid={`season-event-${e.id}`}>
            <td className="py-2">{e.title}<div className="text-xs text-[#475569]">{fmtDate(e.start_at)} · {e.status}</div></td>
            <td className="py-2 text-xs">{e.registration_open ? "Open" : (e.registration_opens_at || s.registration_opens_at) ? `Opens ${fmtDate(e.registration_opens_at || s.registration_opens_at)}` : "Closed"}</td>
            <td className="py-2 text-right">{!e.registration_open && e.status === "published" && <button className="text-xs font-semibold underline" onClick={() => run(() => api.post(`/admin/seasons/${s.slug}/open-now`, { event_id: e.id }), "Registration opened", reload)} data-testid={`event-open-${e.id}`}>Open now</button>}</td>
          </tr>
        ))}
      </tbody></table>
    </div>
  );
};

const VipCard = ({ text, reload }) => {
  const [t, setT] = useState(text);
  return (
    <div className="panel space-y-2 p-5" data-testid="vip-benefits-card">
      <div className="eyebrow">VIP benefits</div>
      <p className="text-xs text-[#475569]">VIP tickets stay off sale while this is empty. Seat limits are set per season above, or per class in its price tiers.</p>
      <Textarea rows={8} value={t} onChange={(e) => setT(e.target.value)} data-testid="vip-benefits-input" />
      <button className="btn-navy" onClick={() => run(() => api.put("/admin/vip-benefits", { benefits: t }), "VIP benefits saved", reload)} data-testid="vip-benefits-save-btn">Save VIP benefits</button>
    </div>
  );
};

const NotifyReport = () => {
  const { data } = useFetch("/admin/reports/notify-me");
  if (!data) return null;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="panel p-5" data-testid="notify-report"><div className="eyebrow mb-2">Notify-me sign-ups by city and season</div>
        <table className="w-full text-sm"><tbody>{data.notify.map((r) => <tr key={r.city + r.season} className="border-t"><td className="py-1.5">{r.city}</td><td className="text-xs">{r.season}</td><td className="text-right font-mono-num">{r.total} ({r.consented} consented)</td></tr>)}</tbody></table>
      </div>
      <div className="panel p-5" data-testid="requested-cities-report"><div className="eyebrow mb-2">Cities requested</div>
        <table className="w-full text-sm"><tbody>{data.requested.map((r) => <tr key={r.city} className="border-t"><td className="py-1.5 capitalize">{r.city}</td><td className="text-right font-mono-num">{r.count}</td></tr>)}</tbody></table>
      </div>
    </div>
  );
};

export default function SeasonsAdmin() {
  const { data, loading, reload } = useFetch("/admin/seasons");
  if (loading || !data) return <Loader />;
  return (
    <>
      <PageTitle title="Seasons & pricing" sub="Prices in US dollars, before tax. Registration opens automatically at the set time and emails that city's notify-me list." />
      <div className="space-y-6">
        {data.seasons.map((s) => <SeasonCard key={s.slug} s={s} reload={reload} />)}
        <VipCard text={data.vip_benefits} reload={reload} />
        <NotifyReport />
      </div>
    </>
  );
}
